import React from 'react'
import {AnalyticsQueueContextProvider} from 'contexts/analyticsQueue'
import {ForbiddenError} from 'models'

import {FeatureFlagsResponseDto} from '@invitae/stargate-ts-fetch'

import client, {apiBase} from 'api'
import {AUTH_CREDENTIALS_ERROR_MSG} from 'constants/api'
import {HttpMethod, HttpStatusCode} from 'constants/enums'
import {FEATURE_FLAGS} from 'constants/featureFlags'
import {HCX_INIT, PORTAL_SIGN_IN, PORTAL_URLS, REFRESH_TOKEN_URL, STARGATE_FEATURE_FLAGS} from 'constants/urls'
import {isProductionBuild} from 'utils/env'
import initializeLogRocket from 'utils/initializeLogRocket'
import {resolveUrlWithNextParam} from 'utils/resolveUrlWithNextParam'

import AppLoaderErrorBoundary from './AppLoaderErrorBoundary'
import AppLoaderErrorIndicator from './AppLoaderErrorIndicator'
import AppLoaderLoadingIndicator from './AppLoaderLoadingIndicator'

// Lazy import app (including store) so we can first fetch PORTAL data
const App = React.lazy(() => import('App'))

interface AppLoaderState {
  error: Error | null
  hasError: boolean
  initialized: boolean
  loading: boolean
}

export class AppLoader extends React.Component<unknown, AppLoaderState> {
  state = {
    error: null,
    hasError: false,
    initialized: false,
    loading: true,
  }

  handleForbiddenResponse = async (body: ForbiddenError) => {
    if (body.detail) {
      if (body.detail === AUTH_CREDENTIALS_ERROR_MSG) {
        window.location.replace(resolveUrlWithNextParam(PORTAL_SIGN_IN))
      } else {
        window.location.replace(body.detail)
      }
    }
  }

  async componentDidMount() {
    try {
      const initResponse = await client.fetch(HCX_INIT)
      const body = await initResponse.json()
      if (!initResponse.ok && initResponse.status === HttpStatusCode.FORBIDDEN && body) {
        return this.handleForbiddenResponse(body)
      }

      const featureFlagsResponse = await client.fetch(STARGATE_FEATURE_FLAGS, {
        body: JSON.stringify({
          appName: 'clinician-launchpad',
          context: {
            properties: {
              country: body?.PORTAL?.user?.country,
              organizations: body?.PORTAL?.user?.organization,
            },
            userId: body?.PORTAL?.user?.email,
          },
          flagNames: FEATURE_FLAGS,
          projectName: 'customer-experience',
        }),
        method: HttpMethod.POST,
      })

      const {featureFlags = []} = (await featureFlagsResponse.json()) as FeatureFlagsResponseDto
      // Transform feature flags from Array<FeatureFlagStateDto> to an object
      // keyed by feature flag name with boolean `enabled` as the value
      // This matches the legacy LaunchDarkly feature flags shape
      const flags = featureFlags.reduce(
        (map, flag) => {
          map[flag.name] = flag.enabled
          return map
        },
        {} as Record<string, boolean>,
      )

      window.PORTAL = {
        ...body.PORTAL,
        flags,
      }

      window.TIMEOUT = {
        ...body.TIMEOUT,
        REFRESH_TOKEN_URL: `${apiBase}${REFRESH_TOKEN_URL}`,
        resetTimeoutProperties: () => undefined,
        // Placeholder until set by timeout.js
        SIGNOUT_URL: PORTAL_URLS.SIGN_OUT_URL,
      }

      if (process.env.NODE_ENV === 'production') {
        initializeLogRocket()
      }

      this.setState({
        error: null,
        hasError: false,
        initialized: true,
        loading: false,
      })
    } catch (e) {
      this.setState({
        // add error to state so it may be inspected if needed
        error: e as Error,
        hasError: true,
        initialized: false,
        loading: false,
      })
    }
  }

  /**
   * We have some redundant error handling here, I'll explain:
   *
   *   -  `loading`, `hasError`, and rendering `AppLoaderErrorIndicator` and `AppLoaderLoadingIndicator`
   *      at the top level provides loading spinner and error handling for the initial
   *      /hcx/init data fetch in `componentDidMount`
   *
   *   -  `AppLoaderErrorBoundary` and `React.Suspense` (with fallback) provide _the same loading indicator
   *      and error messaging_, but for the lazy-load of `AppWithStore`
   *
   * The end-user will only see a single loading indicator and a single error if either of these loads fail.
   */
  render() {
    const {hasError, initialized, loading} = this.state
    // TODO: enrich loading indicator & error indicator in SAT-97 / WT-15442
    return (
      <>
        {loading && <AppLoaderLoadingIndicator />}
        {hasError && <AppLoaderErrorIndicator />}
        {initialized && (
          <AppLoaderErrorBoundary>
            <AnalyticsQueueContextProvider debug={!isProductionBuild()}>
              <React.Suspense fallback={<AppLoaderLoadingIndicator />}>
                <App />
              </React.Suspense>
            </AnalyticsQueueContextProvider>
          </AppLoaderErrorBoundary>
        )}
      </>
    )
  }
}

export default AppLoader
